import {
  useGetPackingList,
  useGetSellerProducts,
  usePublishPackingList,
  useSavePackingList,
} from '~/api';
import type { OrderStatus, UnitCode } from '~/graphql/graphql';
import { PackingListView } from '~/pages/order-view/sections/PackingListView';
import type { SellerProduct } from '~/pages/order-view/sections/PackingListView';

export function PackingListSection({
  orderId,
  orderItems,
  orderStatus,
  currency,
  useSellerAddFlow,
}: {
  orderId: string;
  orderItems: Array<{
    platformProductsLengths: {
      id: string;
      isNominal?: boolean | null;
      lengthDisplayValue: string;
      platformProduct: {
        name: string;
        nominalWidthInches?: number | null;
        actualWidthInches?: number | null;
        nominalThicknessInches?: number | null;
        actualThicknessInches?: number | null;
        actualLengthInches?: number | null;
        piecesPerBundle: { value: number; unit: UnitCode };
      };
      length: {
        nominalLengthInches?: number | null;
        actualLengthInches: number;
      };
    };
    price?: { amount: number; currency: string } | null;
    piecesPerBundleOverride?: number | null;
    effectivePiecesPerBundle: number;
    quantity: { value: number; unit: UnitCode };
  }>;
  orderStatus: OrderStatus;
  currency?: string;
  useSellerAddFlow?: boolean;
}) {
  const { data: plData, isLoading: isLoadingPL } = useGetPackingList(orderId);
  const { data: productsData } = useGetSellerProducts({
    enabled: useSellerAddFlow,
  });
  const { mutateAsync: savePackingListAsync, isPending: isSaving } =
    useSavePackingList();
  const { mutateAsync: publishPackingListAsync } = usePublishPackingList();

  const packingList = plData?.packingList ?? null;
  const products: SellerProduct[] = (
    productsData?.sellerProductInventories?.content ?? []
  ).flatMap((inv) => {
    const spc = inv?.sellerProductConfiguration;
    if (!spc?.platformProductLength) return [];
    return [
      {
        platformProductLength: spc.platformProductLength,
        pricePerUnit: spc.price?.pricePerUnit,
      },
    ];
  });

  return (
    <PackingListView
      orderId={orderId}
      orderItems={orderItems}
      orderStatus={orderStatus}
      packingList={packingList}
      isLoadingPL={isLoadingPL}
      products={products}
      isSaving={isSaving}
      useSellerAddFlow={useSellerAddFlow}
      onSavePackingList={savePackingListAsync}
      onPublishPackingList={publishPackingListAsync}
      currency={currency}
    />
  );
}
